import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import styles from './styles/App.module.css';
import './styles/theme.css';
import { NotificationProvider } from './components/NotificationContext';

// Components
// Header component removed to fix duplicate navbar issue
import Footer from './components/layout/Footer/Footer';
import Navbar from './components/layout/Navbar/Navbar';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import Home from './pages/Home/Home';
import About from './pages/About/About';
import Contact from './pages/Contact/Contact';
import Shop from './pages/Shop/Shop';
import Blog from './pages/Blog/Blog';
import BlogPost from './pages/Blog/BlogPost';
import Admin from './pages/Admin';
import Cart from './pages/Cart/Cart';
import OrderConfirmation from './pages/OrderConfirmation/OrderConfirmation';
import Marketer from './pages/Marketer';
import Login from './pages/Login/Login';
import MarketerPage from './pages/MarketerPage';
import Winners from './pages/Winners/Winners';
import ProductDetail from './pages/Shop/ProductDetail';


// Utils
// useEffect(() => {
//   setupAdmin();
// }, []);

function App() {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme === 'dark' : true; // Default to true (dark mode) if no saved preference
  });

  const [cartItems, setCartItems] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    localStorage.setItem('lang', lang);
  }, [lang]);

  useEffect(() => {
    const handleCartUpdate = (event) => {
      const cart = JSON.parse(localStorage.getItem('cart')) || [];
      setCartItems(cart);
    };

    window.addEventListener('cart-updated', handleCartUpdate);
    return () => window.removeEventListener('cart-updated', handleCartUpdate);
  }, []);

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  const addToCart = (product) => {
    setCartItems(prevItems => {
      // Check if product with same ID, size, and color already exists
      const existingItem = prevItems.find(item => 
        item.id === product.id && 
        item.selectedSize === product.selectedSize &&
        item.selectedColor === product.selectedColor
      );

      if (existingItem) {
        // Update quantity for existing item with same size and color
        return prevItems.map(item =>
          item.id === product.id && 
          item.selectedSize === product.selectedSize &&
          item.selectedColor === product.selectedColor
            ? { ...item, quantity: item.quantity + (product.quantity || 1) }
            : item
        );
      } else {
        // Add new item with size, color and quantity
        const cartItem = {
          ...product,
          quantity: product.quantity || 1,
          selectedSize: product.selectedSize || null,
          selectedColor: product.selectedColor || null,
          // Use mainImage if available, otherwise fallback to imageUrl
          imageUrl: product.mainImage || product.imageUrl,
          // Calculate final price
          price: product.isOffer ? product.offerPrice : product.price,
          originalPrice: product.price
        };
        return [...prevItems, cartItem];
      }
    });
  };

  const removeFromCart = (productKey) => {
    setCartItems(prevItems => {
      const [itemId, selectedSize, selectedColor] = productKey.split('-');
      const size = selectedSize === 'nosize' ? null : selectedSize;
      const color = selectedColor === 'nocolor' ? null : selectedColor;
      
      return prevItems.filter(item => 
        !(item.id === itemId && item.selectedSize === size && item.selectedColor === color)
      );
    });
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const updateCartItem = (productId, updates) => {
    setCartItems(prevItems => 
      prevItems.map(item =>
        item.id === productId
          ? { ...item, ...updates }
          : item
      )
    );
  };

  return (
    <NotificationProvider>
      <Router>
        <div className={styles.app}>
          {/* Header component removed to fix duplicate navbar issue */}
          <Navbar 
            isDarkMode={isDarkMode} 
            toggleTheme={toggleTheme}
            cartItems={cartItems}
            lang={lang}
            setLang={setLang}
          />
          
          <main className={styles.main}>
            <Routes>
              <Route path="/" element={<Home lang={lang} setLang={setLang} />} />
              <Route path="/about" element={<About lang={lang} setLang={setLang} />} />
              <Route path="/contact" element={<Contact lang={lang} setLang={setLang} />} />
              <Route path="/winners" element={<Winners lang={lang} setLang={setLang} />} />
              <Route 
                path="/shop" 
                element={
                  <Shop 
                    cartItems={cartItems}
                    addToCart={addToCart}
                    removeFromCart={removeFromCart}
                    clearCart={clearCart}
                    lang={lang}
                    setLang={setLang}
                  />
                } 
              />
              <Route path="/product/:id" element={<ProductDetail onAddToCart={addToCart} lang={lang} />} />
              <Route path="/blog" element={<Blog lang={lang} setLang={setLang} />} />
              <Route path="/blog/:id" element={<BlogPost lang={lang} />} />
              <Route 
                path="/admin/*" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Admin />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/cart" 
                element={
                  <Cart 
                    cartItems={cartItems}
                    removeFromCart={removeFromCart}
                    clearCart={clearCart}
                    updateCartItem={updateCartItem}
                    lang={lang}
                  />
                } 
              />
              <Route path="/order-confirmation" element={<OrderConfirmation lang={lang} />} />
              <Route path="/marketer/:marketerId" element={<MarketerPage />} /> {/* Specific marketer page */}
              <Route 
                path="/marketer" 
                element={
                  <ProtectedRoute allowedRoles={['marketer']}>
                    <Marketer />
                  </ProtectedRoute>
                } 
              />
              <Route path="/login" element={<Login />} />

            </Routes>
          </main>

          <Footer />
        </div>
      </Router>
    </NotificationProvider>
  );
}

export default App;